/** @jsx jsx */
import { jsx } from '@emotion/react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { browserHistory } from '@americanexpress/one-app-router';
import PropTypes from 'prop-types';
import SharedScript from '@americanexpress/shared-scripts';
import { callFxgApi, FxLoadingComponent } from 'axp-fx-common';
import { compose } from 'redux';
import Helmet from 'react-helmet';
import { composeModules } from 'holocron';
import { combineReducers } from 'redux-immutable';
import childRoutes from '../childRoutes';
import userReducer from '../redux/reducers/user';
import helpMenuReducer from '../redux/reducers/help';
import Header from './Header/Header';
import Footer from './Footer/Footer';
import EnrollmentConflictPage from './Common/EnrollmentConflictPage';
import RegisterLayout from './Register/RegisterLayout';
import { isUnprotectedPath } from '../util/pathUtil';
import {
  isAmexSessionReady,
  isAuthenticated,
  closeSessionFrame,
  setOptions,
  login,
} from '../util/amexSession';
import { dispatchLoginData } from '../redux/reducers/user/login';
import { apiPaths, routes } from '../constants/paths';
import labels from '../constants/labels';
import { respCodes, httpStatus } from '../constants/responseCodes';

const moduleName = 'axp-secure-file-express-root';

const REGISTRATION_ERROR_MESSAGE = labels.AMEX_REP_HELP_MESSAGE;

const ENROLLMENT_SUCCESS_HEADING = 'FileXpress Enrollment Completed';

const ENROLLMENT_SUCCESS_MESSAGE =
  'Your FileXpress enrollment has been successfully completed using your existing account. Please log in with the same credentials to continue.';

export const AxpSecureFileExpressRoot = (props) => {
  const { children } = props;

  const [loading, setLoading] = useState(true);
  const [isUserAuthenticated, setUserAuthenticated] = useState(false);
  const [showResultPageOnly, setShowResultPageOnly] = useState(false);
  // Set when enrollment to SFT product returns RESP416 — render the conflict page inline in the shell.
  const [showConflictPage, setShowConflictPage] = useState(false);
  // RSVP/redemption URL returned in the RESP416 response `data` — where the conflict
  // page CTA sends the user to re-enter the sign-up journey.
  const [conflictRedirectUrl, setConflictRedirectUrl] = useState(null);

  const fxgApiHostUrl = useSelector((state) => state.getIn(['config', 'fxgApi']));
  const internetIdentityBaseUrl = useSelector((state) => state.getIn(['config', 'intranetIdentityBaseUrl']));
  const appBaseUrl = useSelector((state) => state.getIn(['config', 'appBaseUrl']));
  const loginData = useSelector((state) => state.getIn([
    'modules',
    'axp-secure-file-express-root',
    'userData',
    'login',
  ]));
  // The app shell renders only once the authenticated user's profile has loaded,
  // which we detect via the presence of their email address.
  const hasUserProfile = Boolean(loginData?.userDetail?.emailAddress);

  const logoutUrl = `${internetIdentityBaseUrl}/logout?DestPage=${encodeURIComponent(appBaseUrl)}`;

  const path = window.location.pathname;
  const isUnprotected = isUnprotectedPath(path);
  const isLocalEnvironment = process.env.NODE_ENV === 'development';

  const sessionTimerRef = useRef(null);
  const dispatch = useDispatch();
  const dispatchUserData = useCallback((data) => dispatch(dispatchLoginData(data)), [dispatch]);

  /**
   * Enrollment to SFT product for the authenticated user, then load their profile.
   *
   * Enrollment to SFT product (POST /profile):
   *  - RESP000 → enrollment to SFT product succeeded / registration completed; fetch the user profile.
   *      - user API succeeds → dispatch user data and render the app (do nothing else).
   *      - user API fails with 401 → account is enrolled but the current session
   *        can't yet load it; show the enrollment completed page (log in again).
   *      - any other user API failure → error page.
   *  - RESP001 → enrollment to SFT product was previously completed; fetch the user
   *      profile and, on success, render the app (on a 401 it falls through to the error page).
   *  - RESP417 → enrollment to SFT product succeeded but the sign-up token could not
   *      be updated; show the enrollment completed page immediately (no user API call).
   *  - RESP416 → enrollment to SFT product conflict; show the inline conflict page
   *      whose CTA redirects the user to the RSVP/redemption URL from the response
   *      `data` (falling back to /register) to re-enter the sign-up journey.
   *  - anything else / thrown → generic error page.
   */
  const registerAndGetUserDetails = useCallback(async () => {
    setShowConflictPage(false);
    setConflictRedirectUrl(null);
    setShowResultPageOnly(false);
    setLoading(true);

    // Show a result page (success/error) in place of the app shell.
    const showResultPage = (pathname, state) => {
      setShowResultPageOnly(true);
      setLoading(false);
      browserHistory.push({ pathname, state });
    };

    // "FileXpress enrollment completed — please log in again" result page,
    // shown inside the registration banner layout.
    const showEnrollmentCompletedPage = () => showResultPage('success', {
      heading: ENROLLMENT_SUCCESS_HEADING,
      successMessage: ENROLLMENT_SUCCESS_MESSAGE,
      buttonLabel: labels.LOGIN_TO_FX,
      // "Login to FileXpress" logs the sign-up session out first; identity then
      // lands the user on the FileXpress app (DestPage=appBaseUrl) via its iframe.
      // Because buttonHref is a logout URL, SuccessMessage also logs the user out
      // silently after 10 seconds (see useAutoLogout).
      buttonHref: logoutUrl,
      withBanner: true,
    });

    // On a session-level error, "Try Again" restarts the flow via logout → login.
    const showErrorPage = () => showResultPage('error', {
      errorMessage: REGISTRATION_ERROR_MESSAGE,
      buttonHref: logoutUrl,
    });

    let registrationRespCode;
    try {
      const {
        status: regStatus,
        response: regResponse,
      } = await callFxgApi(`${fxgApiHostUrl}${apiPaths.productEnroll}`, 'POST', null);

      registrationRespCode = regResponse?.respCd;

      // HTTP 200 → enrollment to SFT product succeeded.
      if (regStatus === httpStatus.OK) {
        // RESP417 — enrolled, but the authorization token could not be updated;
        // surface the "enrollment completed, log in again" page (no user API call,
        // there is no usable session to load).
        if (registrationRespCode === respCodes.ENROLLED_RELOGIN_REQUIRED) {
          showEnrollmentCompletedPage();
          return;
        }
        // Otherwise (RESP000) proceed to fetch the user profile below.
      } else if (regStatus === httpStatus.BAD_REQUEST && regResponse?.errorCd === respCodes.CONFLICT) {
        // HTTP 400 + RESP416 — enrollment conflict; show the inline conflict page.
        // The response `data` carries the RSVP/redemption URL to re-enter sign-up.
        setConflictRedirectUrl(regResponse?.data || null);
        setShowConflictPage(true);
        setLoading(false);
        return;
      } else {
        // Any other outcome (500 / RESP500 / unexpected) — show the error page.
        showErrorPage();
        return;
      }
    } catch {
      showErrorPage();
      return;
    }

    // Enrollment to SFT product succeeded (HTTP 200, RESP000) — fetch the user profile.
    const { response, hasErrors, status } = await callFxgApi(`${fxgApiHostUrl}${apiPaths.user}`, 'GET', null);

    // Logged-in session already active — load the user and render the app.
    if (!hasErrors) {
      dispatchUserData(response?.data);
      setLoading(false);
      return;
    }

    // Enrolled to SFT product but not yet logged in: for a fresh RESP000 enrollment
    // this is the expected "please log in again to continue" state.
    if (status === httpStatus.UNAUTHORIZED && registrationRespCode === respCodes.SUCCESS) {
      showEnrollmentCompletedPage();
      return;
    }

    showErrorPage();
  }, [fxgApiHostUrl, logoutUrl, dispatchUserData]);

  /**
   * Conflict page CTA — log the user out first, then land them on the
   * RSVP/redemption URL from the RESP416 response `data` so they re-enter the
   * sign-up journey (Welcome page) with a fresh session. Logging out first
   * prevents the existing login cookie from being reused for the new account.
   * Falls back to the registration route when `data` is absent.
   */
  const onConflictContinueClick = useCallback(() => {
    const destination = conflictRedirectUrl || `${appBaseUrl}${routes.register}`;
    window.location.assign(`${internetIdentityBaseUrl}/logout?DestPage=${encodeURIComponent(destination)}`);
  }, [conflictRedirectUrl, appBaseUrl, internetIdentityBaseUrl]);

  // Once authenticated on a protected route: register the user, then load their profile
  useEffect(() => {
    if (!isUserAuthenticated || isUnprotected) return undefined;

    registerAndGetUserDetails();
    return undefined;
  }, [isUserAuthenticated, isUnprotected, registerAndGetUserDetails]);

  // Poll for AmexSession; set options once available, then handle auth
  useEffect(() => {
    // In local/unprotected mode AmexSession is not required — skip polling entirely
    if (isUnprotected || isLocalEnvironment) {
      setUserAuthenticated(true);
      setLoading(false);
      return undefined;
    }

    const loginResponseHandler = ({ statusCode }) => {
      closeSessionFrame();
      if (statusCode === 0) {
        setUserAuthenticated(true);
      } else {
        window.location.reload();
      }
    };

    const checkSession = () => {
      if (!isAmexSessionReady()) return;

      clearInterval(sessionTimerRef.current);

      setOptions({
        target: encodeURIComponent(logoutUrl),
        locale: 'en-US',
      });

      if (isAuthenticated()) {
        closeSessionFrame();
        setUserAuthenticated(true);
      } else {
        login({
          autoClose: true,
          style: 'light',
          callback: loginResponseHandler,
        });
      }
    };

    sessionTimerRef.current = setInterval(checkSession, 1000);
    return () => clearInterval(sessionTimerRef.current);
  }, []);

  const loader = (
    <div
      className="flex flex-justify-center flex-align-items-center"
      style={{ minHeight: '100vh' }}
    >
      <FxLoadingComponent />
    </div>
  );

  const renderContent = () => {
    if (loading) return loader;

    if (showConflictPage) {
      return (
        <main style={{ minHeight: '380px' }}>
          <RegisterLayout centerContent>
            <EnrollmentConflictPage onContinueClick={onConflictContinueClick} />
          </RegisterLayout>
        </main>
      );
    }

    if (showResultPageOnly) {
      return <main style={{ minHeight: '380px' }}>{children}</main>;
    }

    // App shell — only once the authenticated user's profile has loaded.
    // Unprotected/local routes never fetch a profile, so they render the shell too.
    if (hasUserProfile || isUnprotected || isLocalEnvironment) {
      return (
        <>
          <Header isAuthenticated={isUserAuthenticated} isUnprotected={isUnprotected} />
          <main style={{ minHeight: '380px' }}>{children}</main>
          {isUserAuthenticated && <Footer />}
        </>
      );
    }

    return loader;
  };

  return (
    <React.Fragment>
      <Helmet
        link={[{
          rel: 'stylesheet',
          href: 'https://www.aexp-static.com/cdaas/one/statics/@americanexpress/dls/6.25.4/package/dist/6.25.4/styles/dls.min.css',
        }]}
        script={[{
          type: 'text/javascript',
          src: 'https://www.aexp-static.com/cdaas/one/statics/@americanexpress/dls/6.25.4/package/dist/6.25.4/scripts/dls.min.js',
        }]}
      />

      <SharedScript name="one-identity-session" version="^1.0.0" windowCheck="AmexSession" />

      <div style={{ minWidth: '1024px' }}>
        {renderContent()}
      </div>
    </React.Fragment>
  );
};

AxpSecureFileExpressRoot.propTypes = {
  locale: PropTypes.string.isRequired,
  children: PropTypes.node,
};

AxpSecureFileExpressRoot.childRoutes = childRoutes;

/* istanbul ignore next */
if (!global.BROWSER) {
  AxpSecureFileExpressRoot.appConfig = require('../config/appConfig').default;
}

export const load = () => (dispatch) => dispatch(composeModules([{ name: 'axp-secure-file-express-portal' }]));

export const loadModuleData = async ({ store }) => {
  const { dispatch } = store;
  await dispatch(loadLanguagePack(moduleName, { fallbackLocale: 'en-US' }));
};

AxpSecureFileExpressRoot.holocron = {
  name: moduleName,
  reducer: combineReducers({
    userData: userReducer,
    helpMenuData: helpMenuReducer,
  }),
  loadModuleData,
  load,
};

const hocChain = compose(oneAppModuleWrapper(moduleName));

export default hocChain(AxpSecureFileExpressRoot);
